import { type ComponentProps, type FC, type ReactNode } from 'react'
import {
  Controller,
  type RegisterOptions,
  type UseFormReturn,
} from 'react-hook-form'

import { FormControl, FormHelperText, FormLabel, Option, Select } from 'base-ui'

import { type MarketplacePartnerRegisterPageFormV3Values } from '../../../hooks/marketplacePartnerRegisterPageFormV3RHFModel'

type MarketplacePartnerRegisterPageFormV3SelectFieldName = {
  [
    Key in keyof MarketplacePartnerRegisterPageFormV3Values
  ]: MarketplacePartnerRegisterPageFormV3Values[Key] extends string
    ? Key
    : never
}[keyof MarketplacePartnerRegisterPageFormV3Values]

type MarketplacePartnerRegisterPageFormV3SelectFieldProps = {
  fieldName: MarketplacePartnerRegisterPageFormV3SelectFieldName
  form: UseFormReturn<MarketplacePartnerRegisterPageFormV3Values>
  label?: ReactNode
  optionEntries?: {
    label: string
    value: string
  }[]
  required?: boolean
  rules?: RegisterOptions<
    MarketplacePartnerRegisterPageFormV3Values,
    MarketplacePartnerRegisterPageFormV3SelectFieldName
  >
}

type SelectOnChange = NonNullable<ComponentProps<typeof Select>['onChange']>

export const MarketplacePartnerRegisterPageFormV3SelectField: FC<
  MarketplacePartnerRegisterPageFormV3SelectFieldProps
> = ({ fieldName, form, label, optionEntries, required = false, rules }) => {
  const fieldId = `marketplace-partner-register-page-form-select-field-select-${fieldName}`

  return (
    <Controller
      control={form.control}
      name={fieldName}
      render={({ field, fieldState }) => {
        const handleSelectChange: SelectOnChange = (_event, value): void => {
          if (typeof value !== 'string') {
            return
          }

          field.onChange(value)
        }

        return (
          <FormControl
            data-qa-id={`sellerForm-formControl-${fieldName}`}
            error={Boolean(fieldState.error)}
            id={`marketplace-partner-register-page-form-select-field-form-control-${fieldName}`}
            value={field.value}
          >
            <FormLabel htmlFor={fieldId}>
              {label}
              {required ? '*' : ''}
            </FormLabel>
            <Select
              data-qa-id={`sellerForm-select-${fieldName}`}
              id={fieldId}
              onChange={handleSelectChange}
              slotProps={{
                root: {
                  'aria-labelledby': fieldId,
                },
              }}
              title="marketplace-partner-register-page-form-select-field"
              value={field.value}
            >
              {optionEntries?.map((option) => (
                <Option key={option.value} value={option.value}>
                  {option.label}
                </Option>
              ))}
            </Select>
            {fieldState.error?.message ? (
              <FormHelperText
                data-qa-id={`global-MessageForm-selectInput-error-msg-${fieldName}`}
                error
              >
                {fieldState.error.message}
              </FormHelperText>
            ) : null}
          </FormControl>
        )
      }}
      rules={rules}
    />
  )
}
